import React, { useContext } from 'react';

import { Form, FormGroup, InputGroup, Well } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { AxiosError } from 'axios';

import { ActivityContext } from '../../../contexts/Activity';
import InfoNote from '../../utils/InfoNote';

type Props = {
  refund: {
    amount: number;
  };
  reverse: {
    amount: number;
    allowed: boolean;
  };
  onOrderUpdate(reasonNote: string): Promise<void>;
  onClose(): void;
};

type FormData = {
  notes?: string;
};
type ValidFormData = {
  notes: string;
};

export default function UpdateOrderAndRequestRefundPage(props: Props) {
  const activityContext = useContext(ActivityContext);

  const {
    formState: { errors, isValid, isSubmitting, isSubmitSuccessful },
    clearErrors,
    handleSubmit,
    register,
    setError,
    trigger,
  } = useForm<FormData, undefined, ValidFormData>({
    defaultValues: {},
    mode: 'onChange',
  });

  const handleUpdateOrderAndRequestRefundClick = async (
    data: ValidFormData
  ) => {
    clearErrors();
    try {
      await props.onOrderUpdate(data.notes);
      props.onClose();
    } catch (e) {
      const error = e as AxiosError;
      if (error.isAxiosError) {
        setError('root.server', {
          type: (error.response?.status || 500).toString(),
          message: error.response?.data,
        });
      } else {
        setError('root.server', {
          type: '500',
          message: 'Error occured during partial refund submission',
        });
      }

      // hack to unblock 'Refund' button after server error
      // react-hook-form set 'isValid' to false when 'setError' called manually
      // but fields validation (since everything is valid), mark form state as valid again
      // 'trigger' is re-validate all fields
      trigger();
    }
  };

  return (
    <Form
      id='updateOrderAndRequestRefundModalForm'
      onSubmit={handleSubmit(handleUpdateOrderAndRequestRefundClick)}
    >
      <header>
        <h4>Request a refund</h4>
      </header>
      <main>
        <div className='request-refund-modal__page-content'>
          {activityContext.cancellationPolicy && (
            <Well>
              <h4>Cancellation Policy</h4>
              {activityContext.cancellationPolicy}
            </Well>
          )}
          <section className='request-refund-modal__form'>
            <FormGroup>
              <p>
                Refund reason: <i>Requested by customer</i>
              </p>
            </FormGroup>
            <FormGroup>
              <p>Amount to refund user:</p>
              <InputGroup>
                <input
                  type='number'
                  className='form-control'
                  disabled
                  value={props.refund.amount / 100}
                />
                <InputGroup.Addon>$</InputGroup.Addon>
              </InputGroup>
            </FormGroup>

            {props.reverse.allowed && (
              <FormGroup>
                <p>Amount to reverse from vendor:</p>
                <InputGroup>
                  <input
                    type='number'
                    className='form-control'
                    value={props.reverse.amount / 100}
                    disabled
                  />
                  <InputGroup.Addon>$</InputGroup.Addon>
                </InputGroup>
              </FormGroup>
            )}
            {props.reverse.allowed === false && (
              <InfoNote variant='info' faIcon='fa-info-circle'>
                <p>
                  Reversing funds from the vendor isn&apos;t supported for BACH
                  orders.
                </p>
                <p>Only the refund to the customer will be requested.</p>
              </InfoNote>
            )}

            <FormGroup>
              <p>Notes:</p>
              <textarea
                rows={3}
                className='form-control'
                {...register('notes', {
                  required: {
                    value: true,
                    message: 'Notes are required',
                  },
                })}
              />
              {errors.notes?.message && (
                <small className='text-danger'>{errors.notes?.message}</small>
              )}
            </FormGroup>
          </section>
        </div>
      </main>
      <footer className='modal__footer'>
        <menu className={`modal__actions modal__actions--center`}>
          <button
            key='updateOrderAndRequestRefund'
            className='btn btn-primary btn--wide'
            type='submit'
            form='updateOrderAndRequestRefundModalForm'
            disabled={!isValid || isSubmitting || isSubmitSuccessful}
          >
            Update Order and Request Refund
          </button>
        </menu>
      </footer>
    </Form>
  );
}
